import { z } from "zod";

export const PDF_MIME_TYPE = "application/pdf";

export const IMAGE_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/gif",
] as const;

export const EXCEL_MIME_TYPES = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  "application/vnd.ms-excel", // .xls
] as const;

export const WORD_MIME_TYPES = [
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
  "application/msword", // .doc
] as const;

export const TEXT_MIME_TYPE = "text/plain"; // .txt
export const CSV_MIME_TYPE = "text/csv"; // .csv

export const EMAIL_MIME_TYPES = [
  "application/vnd.ms-outlook", // .msg
  "message/rfc822", // .eml
] as const;

export const CAD_MIME_TYPES = [
  "application/stp",
  "application/step",
  "model/step",
] as const;

/**
 * Maps file extensions to MIME types for formats that browsers don't recognize.
 * Used as a fallback when the browser's File.type returns an empty string.
 */
const EXTENSION_TO_MIME_TYPE: Record<string, string> = {
  ".msg": "application/vnd.ms-outlook",
  ".eml": "message/rfc822",
  ".stp": "application/stp",
  ".step": "application/step",
};

/**
 * Resolves a MIME type for a file, falling back to extension-based lookup
 * when the browser-provided type is empty.
 * @param fileName - The file name including extension
 * @param browserFileType - The MIME type from the browser's File.type API
 * @returns The resolved MIME type, or undefined if unknown
 */
export function resolveFileType(
  fileName: string,
  browserFileType: string,
): string | undefined {
  if (browserFileType) {
    return browserFileType;
  }
  const extension = fileName.slice(fileName.lastIndexOf(".")).toLowerCase();
  return EXTENSION_TO_MIME_TYPE[extension];
}

/** All MIME types that can be text-extracted for search indexing. */
export const TEXT_EXTRACTABLE_MIME_TYPES = [
  ...EXCEL_MIME_TYPES,
  ...WORD_MIME_TYPES,
  TEXT_MIME_TYPE,
  CSV_MIME_TYPE,
  ...EMAIL_MIME_TYPES,
  ...CAD_MIME_TYPES,
] as const;

/**
 * Checks if the given MIME type is text-extractable for search indexing.
 * @param mimeType - The MIME type string to check
 * @returns True if the MIME type is in the TEXT_EXTRACTABLE_MIME_TYPES list
 */
export function isTextExtractableMimeType(mimeType: string): boolean {
  return (TEXT_EXTRACTABLE_MIME_TYPES as readonly string[]).includes(mimeType);
}

export interface AssetContent {
  fileName: string;
  content: Uint8Array;
  contentType: string;
}

const AssemblyComponentMetadataSchema = z.object({
  quantityPerAssembly: z.string().nullable(),
  description: z.string().nullable(),
  drawingNumber: z.string().nullable(),
  itemNumber: z.string().nullable(),
});

const DrawingItemMetadataSchema = z.object({
  itemNumber: z.string().nullable(),
  itemDescription: z.string().nullable(),
  drawingNumber: z.string().nullable(),
  revision: z.string().nullable(),
  finish: z.string().nullable(),
  material: z.string().nullable(),
  materialThickness: z.string().nullable().optional(),
  assemblyComponents: z.array(AssemblyComponentMetadataSchema),
  shearSizeLength: z.string().nullable(),
  shearSizeWidth: z.string().nullable(),
  shearSizeUnits: z.string().nullable(),
  castWeight: z.string().nullable(),
  itemWeight: z.string().nullable(),
  weightUnits: z.string().nullable(),
  itemSizeLength: z.string().nullable(),
  itemSizeWidth: z.string().nullable(),
  itemSizeHeight: z.string().nullable(),
  itemSizeUnits: z.string().nullable(),
  surfaceArea: z.string().nullable(),
  surfaceAreaUnits: z.string().nullable(),
  notes: z.string().optional(),
});

export type DrawingItemMetadata = z.infer<typeof DrawingItemMetadataSchema>;

// Drawing Metadata
export const DrawingMetadataSchema = z.object({
  items: z.array(DrawingItemMetadataSchema),
});

export type DrawingMetadata = z.infer<typeof DrawingMetadataSchema>;

export const ModelItemMetadataSchema = z.object({
  name: z.string().nullable(),
  volume: z.number().nullable(),
  volumeUnits: z.string().nullable(),
  boundingBoxDimensions: z
    .object({
      x: z.number().nullable(),
      y: z.number().nullable(),
      z: z.number().nullable(),
    })
    .nullable(),
  boundingBoxUnits: z.string().nullable(),
  externalSurfaceArea: z.number().nullable(),
  surfaceAreaUnits: z.string().nullable().optional(),
  wallThickness: z
    .object({
      min: z.number().nullable(),
      max: z.number().nullable(),
      p10: z.number().nullable(),
      p90: z.number().nullable(),
      nominal: z.number().nullable().optional(),
      thickestSection: z.number().nullable().optional(),
      method: z.string().nullable().optional(),
      sampleCount: z.number().nullable().optional(),
      thickestSampleCount: z.number().nullable().optional(),
      histogram: z
        .array(
          z.object({
            thickness: z.number().nullable(),
            count: z.number().nullable(),
          }),
        )
        .nullable(),
    })
    .nullable(),
  draftAngles: z
    .object({
      hasNegativeDraft: z.boolean().nullable(),
      undercuts: z.number().nullable(),
      minDraftAngle: z.number().nullable(),
    })
    .nullable(),
  assemblyComponents: z.array(
    z.object({
      name: z.string().nullable(),
      quantity: z.number().nullable(),
    }),
  ),
  notes: z.string().optional(),
});

export type ModelItemMetadata = z.infer<typeof ModelItemMetadataSchema>;

// Model Metadata (3D models)
export const ModelMetadataSchema = z.object({
  items: z.array(ModelItemMetadataSchema),
});

export type ModelMetadata = z.infer<typeof ModelMetadataSchema>;

// Union type for all metadata types
export const AssetMetadataSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("drawing"), data: DrawingMetadataSchema }),
  z.object({ type: z.literal("model"), data: ModelMetadataSchema }),
]);

export type AssetMetadata = z.infer<typeof AssetMetadataSchema>;

// Asset schema
export const AssetSchema = z.object({
  id: z.string(),
  userId: z.string().nullable(),
  organizationId: z.string().nullable(),
  externalUrl: z.string(),
  externalFileId: z.string(),
  fileName: z.string(),
  fileSize: z.number().optional(),
  fileType: z.string().optional(),
  metadata: AssetMetadataSchema.nullable().optional(),
  webViewFileName: z.string().nullable().optional(),
  webViewExternalUrl: z.string().nullable().optional(),
  webViewExternalFileId: z.string().nullable().optional(),
  webViewFileSize: z.number().nullable().optional(),
  webViewFileType: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
});

export type Asset = z.infer<typeof AssetSchema>;

export const FileUploadMetadataSchema = z.object({
  fileName: z.string(),
  fileType: z.string().optional(),
  fileSize: z.number().optional(),
});

export type FileUploadMetadata = z.infer<typeof FileUploadMetadataSchema>;

export const UploadResponseSchema = z.object({
  uploadUrl: z.string(),
  asset: AssetSchema,
});

export type UploadResponse = z.infer<typeof UploadResponseSchema>;

export const BatchUploadRequestSchema = z.object({
  files: z.array(FileUploadMetadataSchema),
});

export type BatchUploadRequest = z.infer<typeof BatchUploadRequestSchema>;

export const BatchUploadResponseSchema = z.object({
  results: z.array(UploadResponseSchema),
});

export type BatchUploadResponse = z.infer<typeof BatchUploadResponseSchema>;

export const MultipartUploadPartSchema = z.object({
  partNumber: z.number().int().min(1).max(10000),
  etag: z.string().min(1),
});

export type MultipartUploadPart = z.infer<typeof MultipartUploadPartSchema>;

export const InitiateMultipartUploadRequestSchema = z.object({
  fileName: z.string().min(1),
  fileType: z.string().optional(),
  fileSize: z.number().int().positive(),
});

export type InitiateMultipartUploadRequest = z.infer<
  typeof InitiateMultipartUploadRequestSchema
>;

export const InitiateMultipartUploadResponseSchema = z.object({
  uploadId: z.string(),
  key: z.string(),
  uploadToken: z.string(),
  partSize: z.number().int().positive(),
  expiresAt: z.string(),
});

export type InitiateMultipartUploadResponse = z.infer<
  typeof InitiateMultipartUploadResponseSchema
>;

export const SignMultipartUploadPartsRequestSchema = z.object({
  uploadToken: z.string().min(1),
  partNumbers: z.array(z.number().int().min(1).max(10000)).min(1).max(100),
});

export type SignMultipartUploadPartsRequest = z.infer<
  typeof SignMultipartUploadPartsRequestSchema
>;

export const SignMultipartUploadPartsResponseSchema = z.object({
  parts: z.array(
    z.object({
      partNumber: z.number().int().min(1).max(10000),
      uploadUrl: z.string(),
    }),
  ),
});

export type SignMultipartUploadPartsResponse = z.infer<
  typeof SignMultipartUploadPartsResponseSchema
>;

export const CompleteMultipartUploadRequestSchema = z.object({
  uploadToken: z.string().min(1),
  parts: z.array(MultipartUploadPartSchema).min(1).max(10000),
});

export type CompleteMultipartUploadRequest = z.infer<
  typeof CompleteMultipartUploadRequestSchema
>;

export const CompleteMultipartUploadResponseSchema = z.object({
  asset: AssetSchema,
});

export type CompleteMultipartUploadResponse = z.infer<
  typeof CompleteMultipartUploadResponseSchema
>;

export const AbortMultipartUploadRequestSchema = z.object({
  uploadToken: z.string().min(1),
});

export type AbortMultipartUploadRequest = z.infer<
  typeof AbortMultipartUploadRequestSchema
>;

/**
 * Schema for assets in action/trigger outputs and UI display.
 * Used for serializing assets to JSON with ISO date strings.
 */
export const OutputAssetSchema = z.object({
  id: z.string().describe("The asset ID"),
  fileName: z.string().describe("The file name"),
  fileSize: z.number().optional().describe("File size in bytes"),
  fileType: z.string().optional().describe("MIME type of the file"),
  createdAt: z
    .string()
    .describe("ISO 8601 date string of when the asset was created"),
});

export type OutputAsset = z.infer<typeof OutputAssetSchema>;

export const GetAssetResponseSchema = z.object({
  ok: z.boolean(),
  data: z.object({
    asset: AssetSchema,
  }),
});

export type GetAssetResponse = z.infer<typeof GetAssetResponseSchema>;
